import React from 'react';
import { colors } from '../styles/tokens';
import { useEsMovil } from '../hooks/useEsMovil';
import { fmtCuenta, fmtInt, fmtMoney, fmtPct, plural } from '../utils/format';
import { GLOSARIO } from '../utils/glosario';
import {
  lineaProcedenciaClientes,
  lineaTickets,
  ProcedenciaClientes,
} from '../utils/datosCierre';

export interface VentaCardsProps {
  /** `data-testid` del contenedor (`venta-cards-global` / `venta-cards-tienda`). */
  testid: string;
  /** Pares/monto $ de contado y crédito. */
  contadoPares: number;
  creditoPares: number;
  contadoMonto: number;
  creditoMonto: number;
  /**
   * `true` cuando Proscai no respondió: las tarjetas de dinero muestran "N/D"
   * (o "calculando…" con `ventaCalculando`) en vez de "$0", que se leería
   * como "vendió cero".
   */
  ventaUnavailable: boolean;
  ventaCalculando?: boolean;
  /**
   * Descuento concedido. `undefined` = la API no lo manda (versión anterior) y
   * la tarjeta no se pinta: un "$0" haría creer que no hubo descuentos.
   * `null` = el dato existe pero el ERP no respondió -> "N/D".
   */
  descuento?: number | null;
  /** Préstamos personales: NO son venta comercial. Mismo criterio que `descuento`. */
  prestamos?: number | null;
  /**
   * Venta a mostrador (sólo Tienda): la que no cae en ninguna tarjeta de
   * vendedor. Va como sub-línea de «Venta total» cuando es distinta de cero.
   */
  mostrador?: { pares: number; monto: number };
  /** Clientes nuevos a crédito (altas). `null` = sin dato. */
  clientesNuevos: number | null;
  /** Créditos ESTRENADOS en el periodo (regla B, 5-oct-2026). Puede ser mayor que las altas. */
  clientesNuevosConVenta: number | null;
  /** La API avisa que la consulta de altas no respondió. */
  clientesNuevosPendiente: boolean;
  /**
   * `true` mientras el reintento automático siga en pie: «calculando…». Al
   * agotarse, la tarjeta se rinde con "N/D" en vez de prometer un dato que no
   * va a llegar. Default = `clientesNuevosPendiente`.
   */
  clientesNuevosCalculando?: boolean;
  /** Ayudas (ícono «i») de «Venta total» y «Clientes nuevos»: cada vista dice lo suyo. */
  notaTotal?: string;
  notaClientes?: string;
  /**
   * Tickets del periodo (SPEC 2026-10-13, punto d): sub-línea de «Venta total»
   * «37,825 tickets · ticket promedio $X». `undefined` = API anterior (no se
   * pinta); `null` = «tickets N/D».
   */
  tickets?: number | null;
  ticketsPrestamo?: number | null;
  /**
   * Procedencia de las altas (SPEC 2026-10-13, punto c): «al cierre del lun
   * 05/10 08:00», «N de M días al cierre» o «a la fecha de cálculo». Ausente =
   * API anterior.
   */
  procedenciaClientes?: ProcedenciaClientes;
  /** El periodo aplicado, para contar los días al cierre de una fuente «mixta». */
  rango?: { desde: string; hasta: string } | null;
  /** Altas sin tarjeta de vendedor (sólo Tienda): sub-línea cuando no es 0. */
  clientesSinVendedor?: number;
}

interface CardDef {
  key: string;
  label: string;
  accent: string;
  /** Cifra grande ya formateada. */
  valor: string;
  /** Sub-línea (o nada). */
  sub?: React.ReactNode;
  nota?: string;
}

/**
 * Ayudas de «Clientes nuevos» (regla B del 5-oct-2026; desde el 19-oct sin el
 * tope de 60 días del alta: la PRIMERA compra a crédito de toda la historia,
 * para cuentas abiertas desde 2024). Global habla en plural porque suma
 * tiendas; Tienda menciona el filtro de colaborador.
 */
export const NOTA_CLIENTES_GLOBAL =
  'Clientes nuevos = cuentas de crédito abiertas en el periodo, compren o no. ' +
  'Estrenaron su crédito = clientes que hicieron su PRIMERA compra a crédito en el periodo ' +
  '(cuentas abiertas desde 2024). Cuentan una sola vez, el día de esa compra, en la ' +
  'tienda donde compraron, aunque la cuenta se haya abierto antes del periodo: por eso puede ' +
  'ser mayor que los nuevos. No cuentan préstamos ni tickets cancelados. Solo clientes ' +
  'identificados; TRE no maneja crédito.';

export const NOTA_CLIENTES_TIENDA =
  'Clientes nuevos = cuentas de crédito abiertas en el periodo en esta tienda, compren o no ' +
  '(con filtro de colaborador, sólo las atribuidas a ese vendedor por el ticket donde compraron). ' +
  '"Estrenaron su crédito" = clientes que hicieron su PRIMERA compra a crédito en el periodo ' +
  '(cuentas abiertas desde 2024). Cuentan una sola vez, el día de esa compra, en la tienda ' +
  'donde compraron, aunque la cuenta se haya abierto antes del periodo: por eso puede ser mayor ' +
  'que los nuevos. No cuentan préstamos ni tickets cancelados. Solo clientes identificados; ' +
  'TRE no maneja crédito.';

export const NOTA_TOTAL_TIENDA =
  'Venta total de la tienda (Proscai). "A mostrador" es SOLO la venta de tickets sin vendedor ' +
  'asignado en Proscai (cuenta genérica de mostrador/casa). Todo vendedor con venta tiene su ' +
  'tarjeta abajo, aunque no haya usado CADER en el periodo. Por eso la suma de las tarjetas de ' +
  'vendedor + mostrador da la venta total.';

/**
 * Ancho de pantalla desde el que las tarjetas caben en UNA fila.
 *
 * Son seis tarjetas de dinero —hasta "$30,140,593" a 26 px— y seis columnas a
 * 1440 px dejan ~180 px de texto por tarjeta: la cifra se sale. Por debajo de
 * este corte van en dos filas de tres, que siempre se llenan (6 = 2 × 3).
 */
export const CORTE_VENTA_UNA_FILA = 1600;

/**
 * Columnas de la rejilla de escritorio para `n` tarjetas.
 *
 * ⚠️ Nunca `auto-fit` (lección «la rejilla que nunca cuadraba»): con un número
 * de tarjetas que no divide la fila, la última queda a medias, con MÁS hueco
 * cuanto más ancha la pantalla. Aquí las columnas dividen siempre a `n`: una
 * fila en pantallas anchas; si no, 3 o 2 por fila según divida.
 */
export function columnasVenta(n: number, unaFila: boolean): number {
  if (unaFila) return n;
  if (n % 3 === 0) return 3;
  if (n % 2 === 0) return 2;
  return n;
}

const estiloEtiqueta: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
};

const estiloMonto: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 800,
  color: colors.texto.primary,
  fontVariantNumeric: 'tabular-nums',
  marginTop: 4,
};

/**
 * LAS TARJETAS DE VENTA DEL TABLERO, las mismas en Global y en Tienda (SPEC
 * `2026-10-12-tableros-homologados.md`, decisión del usuario): Venta contado ·
 * Venta crédito · Venta total · Descuento concedido · Préstamos personales ·
 * Clientes nuevos.
 *
 * Hasta el 12-oct Global tenía seis y Tienda cuatro: Descuento y Préstamos sólo
 * existían en Global, aunque `/dashboard/tienda` ya mandaba los préstamos. Un
 * mismo componente es la única forma de que no vuelvan a separarse.
 *
 * En teléfono, UNA columna: son montos y en media pantalla no caben.
 */
const VentaCards: React.FC<VentaCardsProps> = ({
  testid,
  contadoPares,
  creditoPares,
  contadoMonto,
  creditoMonto,
  ventaUnavailable,
  ventaCalculando = false,
  descuento,
  prestamos,
  mostrador,
  clientesNuevos,
  clientesNuevosConVenta,
  clientesNuevosPendiente,
  clientesNuevosCalculando = clientesNuevosPendiente,
  notaTotal,
  notaClientes,
  tickets,
  ticketsPrestamo,
  procedenciaClientes,
  rango = null,
  clientesSinVendedor,
}) => {
  const esMovil = useEsMovil();
  const unaFila = !useEsMovil(CORTE_VENTA_UNA_FILA - 1);
  const totalMonto = contadoMonto + creditoMonto;
  const totalPares = contadoPares + creditoPares;

  const sinVenta = ventaUnavailable ? (ventaCalculando ? 'calculando…' : 'N/D') : null;
  const lineaDeTickets = lineaTickets(tickets, ticketsPrestamo, totalMonto);
  const dinero = (monto: number | null | undefined): string => sinVenta ?? fmtMoney(monto);

  const estiloSub: React.CSSProperties = {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 3,
    // Cifras: en móvil, el piso de 12 px.
    fontSize: esMovil ? 12 : 11.5,
    fontWeight: 700,
    color: colors.texto.secondary,
  };

  const subPares = (pares: number, pct: number | null) => (
    <div style={estiloSub}>
      <span>{fmtCuenta(pares, 'par', 'pares')}</span>
      {pct !== null && (
        <>
          <span style={{ color: colors.texto.tertiary, fontWeight: 400 }}>·</span>
          <span>{fmtPct(pct)}</span>
        </>
      )}
    </div>
  );

  const cards: CardDef[] = [
    {
      key: 'contado',
      label: GLOSARIO.ventaContado.rotulo,
      accent: colors.serie.contado,
      valor: dinero(contadoMonto),
      sub: sinVenta ? undefined : subPares(contadoPares, totalMonto > 0 ? contadoMonto / totalMonto : null),
    },
    {
      key: 'credito',
      label: GLOSARIO.ventaCredito.rotulo,
      accent: colors.serie.credito,
      valor: dinero(creditoMonto),
      sub: sinVenta ? undefined : subPares(creditoPares, totalMonto > 0 ? creditoMonto / totalMonto : null),
    },
    {
      key: 'total',
      label: GLOSARIO.ventaTotal.rotulo,
      accent: colors.azulClaro,
      valor: dinero(totalMonto),
      nota: notaTotal,
      sub: sinVenta ? undefined : (
        <>
          {subPares(totalPares, null)}
          {/* Tickets (SPEC 2026-10-13, d): como el reporte semanal, préstamos
              incluidos; el promedio los excluye. Contra una API anterior no
              se pinta. */}
          {lineaDeTickets && (
            <div data-testid="venta-tickets" style={{ ...estiloSub, marginTop: 2 }}>
              {lineaDeTickets}
            </div>
          )}
          {/* Se oculta cuando es 0: tras el fix de 2026-08-03 mostrador es sólo
              ticket-sin-agente y en la mayoría de las tiendas vale 0; la línea
              en cero se leía como un dato faltante. */}
          {mostrador && (mostrador.monto > 0 || mostrador.pares > 0) && (
            <div data-testid="venta-mostrador" style={{ ...estiloSub, marginTop: 2 }}>
              {fmtMoney(mostrador.monto)} a mostrador
              <span style={{ color: colors.texto.tertiary, fontWeight: 400 }}>·</span>
              {fmtCuenta(mostrador.pares, 'par', 'pares')}
            </div>
          )}
        </>
      ),
    },
  ];

  // Las dos que explican la diferencia contra el reporte de negocio.
  if (descuento !== undefined) {
    cards.push({
      key: 'descuento',
      label: GLOSARIO.descuento.rotulo,
      accent: colors.serie.negado,
      valor: descuento === null ? sinVenta ?? 'N/D' : dinero(descuento),
      sub:
        sinVenta || descuento === null ? undefined : (
          <div style={estiloSub}>
            {/* Sobre la venta A PRECIO DE LISTA (neta + descuento): sobre la
                neta sobrestimaría el descuento. */}
            {totalMonto > 0
              ? `${fmtPct(descuento / (totalMonto + descuento))} de la venta a precio de lista`
              : 'sin venta en el periodo'}
          </div>
        ),
    });
  }
  if (prestamos !== undefined) {
    cards.push({
      key: 'prestamos',
      label: GLOSARIO.prestamos.rotulo,
      accent: colors.texto.tertiary,
      valor: prestamos === null ? sinVenta ?? 'N/D' : dinero(prestamos),
      sub: sinVenta || prestamos === null ? undefined : <div style={estiloSub}>no es venta comercial</div>,
    });
  }

  // Clientes nuevos A CRÉDITO (conteo, no monto). Con la regla B la segunda
  // cifra puede salir MAYOR que la primera; sin explicación se leería como error.
  const conVenta = clientesNuevosConVenta ?? 0;
  const lineaCierre = clientesNuevosPendiente
    ? null
    : lineaProcedenciaClientes(procedenciaClientes, clientesNuevos, rango);
  cards.push({
    key: 'clientes-nuevos',
    label: GLOSARIO.clientesNuevos.rotulo,
    accent: colors.serie.credito,
    valor: clientesNuevosPendiente
      ? clientesNuevosCalculando
        ? '—'
        : 'N/D'
      : fmtInt(clientesNuevos ?? 0),
    nota: notaClientes,
    sub: (
      <>
        <div
          data-testid="venta-card-clientes-nuevos-sub"
          title={notaClientes}
          style={{
            ...estiloSub,
            color: clientesNuevosPendiente ? colors.texto.tertiary : colors.texto.secondary,
          }}
        >
          {clientesNuevosPendiente
            ? clientesNuevosCalculando
              ? 'calculando…'
              : 'no se pudo calcular'
            : `a crédito · ${fmtInt(conVenta)} ${plural(conVenta, 'estrenó su crédito', 'estrenaron su crédito')}`}
        </div>
        {/* De dónde salen las altas (SPEC 2026-10-13, c): al cierre del correo,
            mixtas o a la fecha de cálculo, con lo que el ERP corrigió después. */}
        {lineaCierre && (
          <div
            data-testid="venta-card-clientes-nuevos-cierre"
            style={{ ...estiloSub, marginTop: 2, fontWeight: 600 }}
          >
            {lineaCierre}
          </div>
        )}
        {!clientesNuevosPendiente && clientesSinVendedor !== undefined && clientesSinVendedor > 0 && (
          <div data-testid="venta-card-clientes-sin-vendedor" style={{ ...estiloSub, marginTop: 2 }}>
            {fmtInt(clientesSinVendedor)} sin vendedor atribuido
          </div>
        )}
      </>
    ),
  });

  const columnas = columnasVenta(cards.length, unaFila);

  return (
    <div
      data-testid={testid}
      style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? 'minmax(0, 1fr)' : `repeat(${columnas}, minmax(0, 1fr))`,
        gap: esMovil ? 12 : 14,
        marginTop: esMovil ? 12 : 14,
      }}
    >
      {cards.map((c) => (
        <div
          key={c.key}
          data-testid={`venta-card-${c.key}`}
          style={{
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            // En móvil el relleno lateral baja de 20 a 16 px: 8 px más para la cifra.
            padding: esMovil ? '14px 16px' : '16px 20px',
            boxShadow: '0 1px 2px rgba(20,28,54,.05)',
            position: 'relative',
            overflow: 'hidden',
            // Un hijo de grid tiene `min-width: auto` y se niega a bajar del
            // ancho de su cifra: sin esto empujaría la rejilla fuera de la pantalla.
            minWidth: 0,
          }}
        >
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: c.accent }} />
          <div style={estiloEtiqueta}>
            {c.label}
            {c.nota && (
              <span
                title={c.nota}
                aria-label={c.nota}
                data-testid={`venta-card-${c.key}-nota`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: esMovil ? 16 : 13,
                  height: esMovil ? 16 : 13,
                  borderRadius: '50%',
                  background: colors.ringTrack,
                  color: colors.texto.tertiary,
                  fontSize: esMovil ? 11 : 9.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  letterSpacing: 0,
                  cursor: 'help',
                }}
              >
                i
              </span>
            )}
          </div>
          <div style={estiloMonto}>{c.valor}</div>
          {c.sub}
        </div>
      ))}
    </div>
  );
};

export default VentaCards;
